import { Box, Button, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { CustomPaper } from '../../../../../../components/CustomPaper'
import { icons } from '../../../../../../utils/icons'
import { Constants } from '../../../../../../utils/constants'
import { useStyles } from './styles'
import type { LeftFragmentProps } from './types'
import type { AssignedUser } from '../../types'

function LeftFragment({
  mode,
  store,
  assigningUser,
  assignedUser,
  approvers,
  onGenerateActionPlan,
}: LeftFragmentProps) {
  const { t } = useTranslation()
  const styles = useStyles()
  const showStore = mode !== 'by-user-without-unit'

  const renderUser = (user: AssignedUser, labelKey: string) => (
    <Box sx={styles.block}>
      <Typography sx={styles.blockTitle}>{t(labelKey)}</Typography>

      <Box sx={styles.approverItem}>
        <Box sx={styles.approverIcon}>{icons('personFilled')}</Box>
        <Box sx={styles.approverItemText}>
          <Typography sx={styles.approverItemName}>{user.fullName}</Typography>
          {!!user.date && (
            <Typography sx={styles.approverItemEmail}>
              {dayjs(user.date).locale('es').format(Constants.dayjsHumanFormat)}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  )

  return (
    <Box sx={styles.container} className="h-full">
      {showStore && !!store && (
        <Box sx={styles.block}>
          <Typography sx={styles.blockTitle}>{t('actionPlan.referencedStore')}</Typography>

          <Box sx={styles.approverItem}>
            <Box sx={styles.approverIcon}>{icons('store')}</Box>
            <Box sx={styles.approverItemText}>
              <Typography sx={styles.approverItemName}>
                {store.keyCode} - {store.name}
              </Typography>

              {!!store.managerName && (
                <Typography sx={styles.approverItemEmail}>
                  {t('general.tags.manager')}: {store.managerName}
                </Typography>
              )}

              {!!store.timeZone && (
                <Typography sx={styles.approverItemEmail}>
                  {t('general.timeZoneDetail', { timeZone: store.timeZone })}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      )}

      {renderUser(assigningUser, 'actionPlan.tags.assigningUser')}

      {!!assignedUser && renderUser(assignedUser, 'actionPlan.tags.assignedUser')}

      <Box sx={styles.block}>
        <Typography sx={styles.blockTitle}>{t('actionPlan.approversTitle')}</Typography>

        <CustomPaper sx={styles.approversContainer}>
          {!!approvers?.length && (
            <Box sx={styles.approversList}>
              {approvers.map((approver) => (
                <Box key={approver.id} sx={styles.approverItem}>
                  <Box sx={styles.approverIcon}>{icons('personFilled')}</Box>
                  <Box sx={styles.approverItemText}>
                    <Typography sx={styles.approverItemName}>{approver.fullName}</Typography>
                    {!!approver.email && (
                      <Typography sx={styles.approverItemEmail}>{approver.email}</Typography>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          )}

          {!approvers?.length && (
            <Typography sx={styles.noData}>{t('actionPlan.noAprovers')}</Typography>
          )}
        </CustomPaper>
      </Box>

      <Box sx={styles.actionsContainer}>
        <Button
          fullWidth
          variant="contained"
          color="primary"
          sx={styles.generateButton}
          onClick={onGenerateActionPlan}
        >
          {t('actionPlan.generateActionPlan')}
        </Button>
      </Box>
    </Box>
  )
}

export default LeftFragment
